import React, { useEffect, useState } from 'react';
import { useUserProvider } from '../User/userProvider';
import { ISocials } from '../../pages/User/UserHeader/editSocials';
import { useBlockchainProvider } from '../Blockchain/blockchainProvider';
import { ethers } from 'ethers';

interface IUserEditor {
	avatar: string | null;
	color: string | null;
	description: string | null;
	nickname: string | null;
	socials: ISocials | null;
	setNickname: React.Dispatch<React.SetStateAction<string | null>>;
	setAvatar: React.Dispatch<React.SetStateAction<string | null>>;
	setColor: React.Dispatch<React.SetStateAction<string | null>>;
	setDescription: React.Dispatch<React.SetStateAction<string | null>>;
	setSocials: React.Dispatch<React.SetStateAction<ISocials | null>>;
	updateValues: (values: typeof emptyUserEditorFields) => void;
	isRewardEligible: boolean;
}

export const emptyUserEditorFields = {
	avatar: null,
	color: null,
	description: null,
	nickname: null,
	socials: null,
};

const emptyUserEditor: IUserEditor = {
	...emptyUserEditorFields,
	setNickname: () => null as unknown as void,
	setAvatar: () => null as unknown as void,
	setColor: () => null as unknown as void,
	setDescription: () => null as unknown as void,
	setSocials: () => null as unknown as void,
	updateValues: () => null as unknown as void,
	isRewardEligible: false,
};

export const UserEditorProvider = ({ children }: { children: React.ReactNode }) => {
	const [nicknameNew, setNicknameNew] = useState(emptyUserEditor.nickname);
	const [avatarNew, setAvatarNew] = useState(emptyUserEditor.avatar);
	const [colorNew, setColorNew] = useState(emptyUserEditor.color);
	const [descriptionNew, setDescriptionNew] = useState(emptyUserEditor.description);
	const [socialsNew, setSocialsNew] = useState(emptyUserEditor.socials);

	const [isRewardEligible, setIsRewardEligible] = useState(emptyUserEditor.isRewardEligible);

	const userData = useUserProvider();
	const { contracts, account, provider } = useBlockchainProvider();

	const updateValues = (values: typeof emptyUserEditorFields) => {
		setNicknameNew(values.nickname);
		setAvatarNew(values.avatar);
		setColorNew(values.color);
		setDescriptionNew(values.description);
		setSocialsNew(values.socials);
	};

	useEffect(() => {
		if (!provider || account === ethers.constants.AddressZero) {
			return;
		}

		(async () =>
			setIsRewardEligible(
				!!contracts.userProfile && !(await contracts.userProfile.callStatic.gotReward(account))
			))();
	}, [provider, account]);

	useEffect(() => updateValues(emptyUserEditorFields), [userData.data]);

	return (
		<UserEditorContext.Provider
			value={{
				nickname: nicknameNew,
				avatar: avatarNew,
				color: colorNew,
				description: descriptionNew,
				socials: socialsNew,
				setNickname: setNicknameNew,
				setAvatar: setAvatarNew,
				setColor: setColorNew,
				setSocials: setSocialsNew,
				setDescription: setDescriptionNew,
				updateValues,
				isRewardEligible,
			}}
		>
			{children}
		</UserEditorContext.Provider>
	);
};

const UserEditorContext = React.createContext<IUserEditor>(emptyUserEditor);

export const useUserEditorProvider = () => {
	return React.useContext(UserEditorContext);
};
